/**
 * Coupon permissions — the CRM twin of the server's
 * `src/rbac/permissions/coupons.permissions.ts`. Keep the two in step.
 *
 * Storage: `access_config.global.coupons.<key>` = "allow" | "deny".
 *   manage — Settings → Coupons (create / edit / switch off / delete codes).
 *   apply  — the Coupon code control on a deal's Payments card.
 *
 * A role saved before coupons existed has no `coupons` block: an ABSENT key
 * falls back to the role's preset, exactly as the server does — Admin and
 * Manager may manage, every working preset (Counsellor, Editor, Custom …) may
 * apply, View-only and Guest get neither. An explicit saved value always wins.
 * Tenant admins bypass.
 */

export type CouponControl = "allow" | "deny";
export type CouponPermissionKey = "manage" | "apply";

export const COUPON_PERMISSION_MODULE = "coupons";

export interface CouponOperationMeta {
  key: CouponPermissionKey;
  label: string;
  description: string;
}

export const COUPON_OPERATIONS: CouponOperationMeta[] = [
  {
    key: "apply",
    label: "Apply coupon codes on deals",
    description:
      "On a deal's Payments tab: choose Discount → Coupon code, check a code and apply or remove it. The discount is worked out by the server from the deal's value.",
  },
  {
    key: "manage",
    label: "Manage coupon codes",
    description:
      "Settings → Coupons: create, edit, switch off and delete codes, and see how often each was used.",
  },
];

const ADMIN = { manage: "allow", apply: "allow" } as const;
const WORKING = { manage: "deny", apply: "allow" } as const;
const READ_ONLY = { manage: "deny", apply: "deny" } as const;

/** Per preset — the server's COUPON_PRESET_DEFAULTS, key for key. */
export const COUPON_PRESET_DEFAULTS: Record<
  string,
  Record<CouponPermissionKey, CouponControl>
> = {
  admin: { ...ADMIN },
  manager: { ...ADMIN },
  editor: { ...WORKING },
  contributor: { ...WORKING },
  counsellor: { ...WORKING },
  custom: { ...WORKING },
  viewer: { ...READ_ONLY },
  guest: { ...READ_ONLY },
};

export function couponPresetDefault(
  presetRole: string | null | undefined,
  key: CouponPermissionKey
): CouponControl {
  const block = (presetRole && COUPON_PRESET_DEFAULTS[presetRole]) || WORKING;
  return block[key];
}

/** What a role effectively holds for one key: saved value, else preset default. */
export function effectiveCouponControl(
  saved: Record<string, unknown> | null | undefined,
  presetRole: string | null | undefined,
  key: CouponPermissionKey
): CouponControl {
  const v = saved?.[key];
  if (v === "allow") return "allow";
  if (v != null) return "deny";
  return couponPresetDefault(presetRole, key);
}
